import { Box, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { VoiceRoomBlock } from '#modules/finder/features/posts/components/Post/VoiceRoomBlock';
import { VoiceObserverNote } from '#modules/finder/features/posts/components/Post/VoiceRoomBlock/VoiceObserverNote';
import { MicDenialNote } from '#features/media/MicDenialNote';
import { useMicPermissionDenied } from '#features/media/useMicPermissionDenied';
import { useVoiceActions } from '#features/voice/hooks/useVoiceActions';
import { useVoiceRoomState } from '#features/voice/hooks/useVoiceRoomState';
import { leaveVoice } from '#features/voice/model/voice';
import { useVoiceJoinIntent } from '#features/voice/model/voiceJoinIntent';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { requestVoiceMicrophone } from '#features/voice/utils/nativeVoiceSession';

type SessionVoiceProps = {
  sessionId: string;
  /** PLATFORM | DISCORD | IN_GAME | NONE. Пусто у старых сессий — считаем платформенным. */
  voiceMode?: string | null;
  startedAt?: string | Date | null;
  /** Сколько слотов в сессии — вместимость комнаты равна им. */
  teamLimit?: number;
  /** Я в составе (принял приглашение) либо автор — иначе вместо блока подпись. */
  canJoin: boolean;
  /** Есть ли свободное место в составе — только для подписи наблюдателю. */
  hasFreeSeats?: boolean;
  /** Может ли наблюдатель вообще попасть в состав — выгнанного сервер не пустит обратно. */
  canJoinSquad?: boolean;
  /** Игра и хозяин сессии — подпись плавающего окна, оно данных поста не видит. */
  gameName?: string | null;
  hostName?: string | null;
  /** Привязан ли Discord — нужно только режиму DISCORD. */
  isDiscordLinked?: boolean;
  onLinkDiscord?: () => void;
  onOpenDiscord?: () => void;
  onMessageHost?: () => void;
  /** Карточка заявки или узкая полоса над перепиской — см. `VoiceRoomBlock`. */
  density?: 'card' | 'bar';
  /** Подписанный вход в голос в общем блоке команды и голоса. */
  showJoinLabel?: boolean;
  emphasis?: 'primary' | 'secondary';
  showJoinedControls?: boolean;
};

/**
 * Связывает блок голосовой комнаты с данными: состояние комнаты и счётчики считает
 * `useVoiceRoomState` (тот же расчёт нужен пилюле, полосе и ярусу комнаты), здесь остаётся
 * микрофон, вход-выход и выбор между блоком и подписью наблюдателю.
 */
export const SessionVoice = ({
  sessionId,
  voiceMode,
  teamLimit = 0,
  canJoin,
  hasFreeSeats = false,
  canJoinSquad = true,
  gameName,
  hostName,
  isDiscordLinked,
  onLinkDiscord,
  onOpenDiscord,
  onMessageHost,
  density = 'card',
  showJoinLabel = false,
  emphasis = 'primary',
  showJoinedControls,
}: SessionVoiceProps) => {
  const { t } = useLingui();
  const { state, isPlatform, inThisRoom, connectedCount, speakingName } = useVoiceRoomState({
    sessionId,
    voiceMode,
    teamLimit,
  });

  const micEnabled = useVoiceStore((s) => s.micEnabled);
  // Подсказка про пробел честна только в режиме рации — блок сам этого не знает.
  const micDenied = useVoiceStore((s) => s.micDenied);

  // Медиа здесь не трогаем: соединением владеет плавающий виджет, он живёт выше роутера. Отсюда
  // только общий выход, который гасит стор — а тот уже роняет соединение.
  const { join, pending } = useVoiceActions();
  const queryClient = useQueryClient();

  /*
    Вход с карточки сессии («2 в голосе, войти»): карточка ведёт сюда с намерением, а нажимает
    кнопку этот же блок, и только когда сам показал бы её: я в составе, голос платформенный,
    комната открыта, я ещё не в ней. В остальных состояниях (расписание, полная, чужая вкладка,
    звонок) намерение просто снимается, и человек видит то же, что увидел бы без карточки.
  */
  const intentSessionId = useVoiceJoinIntent((s) => s.sessionId);
  const clearIntent = useVoiceJoinIntent((s) => s.clear);
  useEffect(() => {
    if (intentSessionId !== sessionId) return;
    if (state === 'empty' || state === 'occupied') {
      if (!canJoin || !isPlatform || inThisRoom || pending) return;
      clearIntent();
      void join(sessionId, { gameName, hostName }).catch(() => undefined);
      return;
    }
    if (state !== 'connecting' && state !== 'reconnecting') clearIntent();
  }, [
    canJoin,
    clearIntent,
    gameName,
    hostName,
    inThisRoom,
    intentSessionId,
    isPlatform,
    join,
    pending,
    sessionId,
    state,
  ]);

  /**
   * Спрашиваем разрешение заранее на карточке, если человек может войти. В узком слоте
   * проверку и предупреждение берёт ярус голоса: его строка кнопок для подсказки не подходит.
   * Вход остаётся доступен, потому что ответу «разрешено» верить нельзя.
   */
  const { denied: micPermissionDenied, recheck: recheckMicPermission } = useMicPermissionDenied(
    canJoin && isPlatform && !inThisRoom && density === 'card'
  );

  if (state === 'inGame') {
    return (
      <Text textStyle="caption" color="fg.secondary">
        {t`post:session.room.voiceInGame`}
      </Text>
    );
  }

  // Наблюдателю блок не показываем вовсе: он всё равно не войдёт, а состояния блока написаны от
  // лица входящего — «заходи первым» звало туда, куда двери нет. Ему остаётся подпись, и только
  // когда ей есть что сказать: либо в голосе уже разговаривают (аргумент вступить или написать
  // автору), либо место свободно и голос его дождётся. Пустой голос в закрытом составе молчит:
  // сигнал, за которым не следует действие, — это шум на первом экране заявки.
  if (!canJoin) {
    // Обещание «откроется после вступления» держится только там, где вступление возможно.
    if (!isPlatform || !canJoinSquad) {
      return null;
    }

    // Живой голос перевешивает всё: он показывает, что заявка не мёртвая. Но при закрытом
    // составе он идёт без хвоста про вступление — вступать некуда.
    if (connectedCount > 0) {
      return <VoiceObserverNote variant={hasFreeSeats ? 'inVoice' : 'inVoiceFull'} />;
    }
    if (!hasFreeSeats) {
      return null;
    }
    /*
      Голос открыт с создания сессии, поэтому наблюдателю говорим одно и то же в любой час:
      голос здесь есть, место в составе есть. Ветка «откроется ближе к старту» ушла вместе с
      часовым замком – обещать открытие того, что уже открыто, незачем.
    */
    return <VoiceObserverNote variant="open" />;
  }

  /*
    Войти в голос можно всегда, даже когда в составе я один (23.09.2026).

    С 21.09 здесь стояло обратное правило: одинокому человеку голос не показывался вовсе, потому
    что «разговаривать не с кем». Оно оказалось неверным дважды.

    Во-первых, войти первым это и есть способ сказать «я уже здесь». Карточка в ленте ровно этим
    и торгует: видно, что кто-то в голосе, и туда идут. Прод за 30 дней: 404 комнаты, где человек
    побывал один, и 100, где собрались двое, - то есть почти каждая вторая встреча начиналась с
    того, что кто-то зашёл первым. Правило отрезало это начало.

    Во-вторых, оно стояло только под `isPlatform`, и Discord его не знал: через Discord тот же
    человек входил первым спокойно. Продукт уже разрешал заходить первым, просто в одном канале
    из двух.
  */

  return (
    <>
      <VoiceRoomBlock
        state={state}
        density={density}
        showJoinLabel={showJoinLabel}
        emphasis={emphasis}
        connectedCount={connectedCount}
        speakingName={speakingName}
        micEnabled={micEnabled}
        // В полосе над перепиской кнопок нет, пока я в этой же комнате: плавающее окно
        // разговора висит рядом с теми же микрофоном и выходом, и они дублировались.
        showJoinedControls={showJoinedControls ?? !(density === 'bar' && inThisRoom)}
        // Карточка обязана отличать «выключил сам» от «не дали»: после системного
        // отказа она писала «you're muted», то есть выдавала запрет за выбор игрока.
        micDenied={inThisRoom && micDenied}
        isBusy={pending}
        onJoin={() => void join(sessionId, { gameName, hostName }).catch(() => undefined)}
        onLeave={() => void leaveVoice(queryClient)}
        onCancel={() => void leaveVoice(queryClient)}
        onRetry={() => void join(sessionId, { gameName, hostName }).catch(() => undefined)}
        onToggleMic={() => void requestVoiceMicrophone(!micEnabled)}
        onLinkDiscord={isDiscordLinked ? undefined : onLinkDiscord}
        onOpenDiscord={isDiscordLinked ? onOpenDiscord : undefined}
        onMessageHost={onMessageHost}
      />
      {/* Ниже блока, а не вместо него: человек с запрещённым микрофоном всё ещё может войти и
          слушать — это законный сценарий, и отбирать его нельзя.

          Отбивка сверху — здесь, а не внутри подписи: подпись приклеена к нижнему краю блока
          комнаты, и линия отделяет её от него. На экране вызова та же подпись лежит отдельной
          карточкой со скруглением, и там эта линия чертила бы поперёк скруглённого угла. */}
      {micPermissionDenied && (
        // В компактном слоте яруса и полосы помещается только действие. Ярус выводит
        // предупреждение отдельной строкой, а полоса ведёт в него по нажатию.
        <Box borderTopWidth="1px" borderColor="border.subtle">
          <MicDenialNote onResolved={recheckMicPermission} withCheckLink />
        </Box>
      )}
    </>
  );
};
